// useIntersectionObserver.ts
import { Ref, useCallback, useEffect, useState } from 'react'

interface UseIntersectionObserverArgs {
  threshold?: number
  triggerOnce?: boolean
}

export type ElementPosition = 'above' | 'visible' | 'below'

export const useIntersectionObserver = <T extends HTMLElement = HTMLElement>({
  threshold = 0.1,
  triggerOnce = false,
}: UseIntersectionObserverArgs = {}): [Ref<T>, boolean, ElementPosition] => {
  const [element, setElement] = useState<T | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState<ElementPosition>('below')

  const ref = useCallback((node: T | null) => {
    setElement(node)
  }, []) as Ref<T>

  useEffect(() => {
    if (!element) return

    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          if (triggerOnce) {
            visibilityObserver.unobserve(entry.target)
          }
        } else if (!triggerOnce) {
          setIsVisible(false)
        }
      },
      { threshold }
    )

    const positionObserver = new IntersectionObserver(
      ([entry]) => {
        const { boundingClientRect } = entry

        let newPosition: ElementPosition

        if (boundingClientRect.bottom <= 0) {
          newPosition = 'above'
        } else if (boundingClientRect.top >= window.innerHeight) {
          newPosition = 'below'
        } else {
          newPosition = 'visible'
        }

        setPosition((prev) => (prev === newPosition ? prev : newPosition))
      },
      { threshold: 0 }
    )

    visibilityObserver.observe(element)
    positionObserver.observe(element)

    return () => {
      visibilityObserver.disconnect()
      positionObserver.disconnect()
    }
  }, [element, threshold, triggerOnce])

  return [ref, isVisible, position]
}
